import { formatHour } from '@/utils/formatters';
import {
  Box,
  DialogActions,
  DialogContent,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// denne deles PT af to portfolio analysis komponenter her i remote, men eksporteres også til containeren.
// bør derfor på sigt flyttes til et mere generelt sted, hvor den kan genbruges af flere komponenter i containeren.

const fallbackDaysOfWeek = {
  monday: true,
  tuesday: true,
  wednesday: true,
  thursday: true,
  friday: true,
  saturday: false,
  sunday: false,
};

const EnergyAnalyzerFilterInput = ({
  handleApply,
  setComparisonFilters,
  viewMode,
  context,
  selectedEnergyGroup,
  startHour,
  endHour,
}) => {
  const [hoursFilter, setHoursFilter] = useState(undefined);
  const [daysFilter, setDaysFilter] = useState(undefined);
  const [holidaysFilter, setHolidaysFilter] = useState(undefined);
  const { t } = useTranslation('energyxplorer-analysis-tabs');

  // Use actual daysOfWeek if available, otherwise fallback
  const daysOfWeek =
    selectedEnergyGroup?.options?.daysOfWeek ?? fallbackDaysOfWeek;

  useEffect(() => {
    handleApply({
      activeHoursFilter: hoursFilter,
      activeDaysFilter: daysFilter,
      activeHolidaysFilter: holidaysFilter,
    });

    setComparisonFilters({
      activeHoursFilter: hoursFilter,
      activeDaysFilter: daysFilter,
      activeHolidaysFilter: holidaysFilter,
    });
  }, [hoursFilter, daysFilter, holidaysFilter]);

  const renderToggleGroup = (label, value, setValue, options) => (
    <Box mb={2} sx={{ display: 'flex', flexDirection: 'column' }}>
      <Typography variant="subtitle2" gutterBottom>
        {label}
      </Typography>
      <ToggleButtonGroup
        value={stringify(value)}
        exclusive
        onChange={(_, newValue) => setValue(parseNullable(newValue))}
        fullWidth
        // color="primary"
      >
        {options.map(({ label, value }) => (
          <ToggleButton
            key={value}
            value={value}
            disabled={viewMode === 'line'}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            {label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Box>
  );

  return (
    <>
      <DialogContent>
        {context !== 'hourAnalysis' &&
          renderToggleGroup(
            // t('filter_labels.hours'),
            'Timer med aktivitet valgt i opsætning',
            hoursFilter,
            setHoursFilter,
            [
              // { label: t('filter_labels.no_filter_applied'), value: '' },
              { label: 'Alle', value: '' },
              {
                label: `${formatHour(startHour)} - ${formatHour(endHour)}`,
                value: 'true',
              },
              {
                label: `${formatHour(endHour)} - ${formatHour(startHour)}`,
                value: 'false',
              },
            ],
          )}

        {renderToggleGroup(
          'Dage med aktivitet valgt i opsætning',
          daysFilter,
          setDaysFilter,
          [
            // { label: t('filter_labels.no_filter_applied'), value: '' },
            { label: 'Alle', value: '' },
            {
              label: (
                <Box
                  sx={{
                    display: 'flex',
                    gap: 0,
                    justifyContent: 'center',
                    flexWrap: 'wrap',
                  }}
                >
                  {[
                    { key: 'monday', label: 'M' },
                    { key: 'tuesday', label: 'T' },
                    { key: 'wednesday', label: 'O' },
                    { key: 'thursday', label: 'T' },
                    { key: 'friday', label: 'F' },
                    { key: 'saturday', label: 'L' },
                    { key: 'sunday', label: 'S' },
                  ].map(({ key, label }) => (
                    <ToggleButton
                      key={key}
                      value={key}
                      disabled={true}
                      selected={daysOfWeek[key]} // for the first group
                      sx={{
                        width: 16,
                        height: 16,
                        borderRadius: 0,
                        fontWeight: 'bold',
                        fontSize: '0.60rem',
                        p: 0,
                        '&.Mui-selected': {
                          backgroundColor: 'gray',
                          color: 'white',
                        },
                      }}
                    >
                      {label}
                    </ToggleButton>
                  ))}
                </Box>
              ),
              value: 'true',
            },
            {
              label: (
                <Box
                  sx={{
                    display: 'flex',
                    gap: 0,
                    justifyContent: 'center',
                    flexWrap: 'wrap',
                  }}
                >
                  {[
                    { key: 'monday', label: 'M' },
                    { key: 'tuesday', label: 'T' },
                    { key: 'wednesday', label: 'O' },
                    { key: 'thursday', label: 'T' },
                    { key: 'friday', label: 'F' },
                    { key: 'saturday', label: 'L' },
                    { key: 'sunday', label: 'S' },
                  ].map(({ key, label }) => (
                    <ToggleButton
                      key={key}
                      value={key}
                      disabled={true}
                      selected={!daysOfWeek[key]} // for the second group
                      // onChange={() => handleDayToggle(key)}
                      sx={{
                        width: 14,
                        height: 14,
                        borderRadius: 0,
                        fontWeight: 'bold',
                        fontSize: '0.60rem',
                        p: 0,
                        '&.Mui-selected': {
                          backgroundColor: 'gray',
                          color: 'white',
                          // '&:hover': {
                          //   backgroundColor: darken('#4CAF50', 0.1),
                          // },
                        },
                      }}
                    >
                      {label}
                    </ToggleButton>
                  ))}
                </Box>
              ),
              value: 'false',
            },
          ],
        )}

        {renderToggleGroup(
          t('filter_labels.holidays'),
          holidaysFilter,
          setHolidaysFilter,
          [
            // { label: t('filter_labels.no_filter_applied'), value: '' },
            { label: 'Alle', value: '' },
            { label: 'Kun helligdage', value: 'true' },
            { label: 'Ingen helligdage', value: 'false' },
          ],
        )}
      </DialogContent>
      <DialogActions></DialogActions>
    </>
  );
};

const parseNullable = (val) => {
  if (val === 'true') return true;
  if (val === 'false') return false;
  return undefined;
};

const stringify = (val) => {
  if (val === true) return 'true';
  if (val === false) return 'false';
  return '';
};

export default EnergyAnalyzerFilterInput;
